import * as React from "react";
import { Navigate, useLocation } from "react-router";
import { Skeleton } from "@academia-hub/ui";
import { useAuth } from "./use-auth.js";

const AccountSetupPage = React.lazy(() => import("../pages/auth/AccountSetupPage.js").then((m) => ({ default: m.AccountSetupPage })));

function LoadingScreen() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6">
      <div className="flex w-full max-w-sm flex-col gap-3">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  );
}

/**
 * Route guard for every screen behind `AppLayout` — `07_UI_UX_SPEC.md` §24:
 * "Expired session: ... route to login and preserve intended return path
 * where safe." `status === "loading"` covers both the initial page-load
 * bootstrap and the brief window while a silent refresh is in flight.
 *
 * A restricted account (ADR-0026 §15: must change its password, or set up required MFA) gets the
 * forced setup screen instead of the app, on whatever URL it is. The API refuses it everything
 * else anyway; once the step is done the app renders at that same URL.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status, restriction, signedOut } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <LoadingScreen />;
  }

  if (status === "unauthenticated") {
    // After "Sign out" the login page starts clean: the page just left is not the next person's to land on.
    if (signedOut) return <Navigate to="/login" replace />;
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (restriction) {
    return (
      <React.Suspense fallback={<LoadingScreen />}>
        <AccountSetupPage restriction={restriction} />
      </React.Suspense>
    );
  }

  return children;
}
